import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  CourierSummary,
  fetchMe,
  logout as logoutApi,
} from 'features/courier-auth/services/courierAuthService';

interface CourierAuthContextValue {
  courier: CourierSummary | null;
  loading: boolean;
  refresh: () => Promise<void>;
  setCourier: (c: CourierSummary | null) => void;
  logout: () => Promise<void>;
}

const Ctx = createContext<CourierAuthContextValue | undefined>(undefined);

export const CourierAuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [courier, setCourier] = useState<CourierSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const me = await fetchMe();
      setCourier(me);
    } catch {
      setCourier(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await logoutApi();
    } catch {
      // ignore - clearing local state below is the source of truth
    }
    setCourier(null);
  }, []);

  const value = useMemo(
    () => ({ courier, loading, refresh, setCourier, logout }),
    [courier, loading, refresh, logout],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useCourierAuth = (): CourierAuthContextValue => {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error('useCourierAuth must be used inside CourierAuthProvider');
  }
  return ctx;
};
